import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { GOLD, fmt, Card, Field, Row } from './shared';

export const EstimateTab = ({ api, jobId, readOnly }) => {
  const [est, setEst] = useState(null);
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    api.get(`/accounting/jobs/${jobId}/estimate`).then(r => {
      setEst(r.data.estimate); setCalc(r.data.calculations);
    }).catch(() => toast.error('Failed to load estimate'));
  }, [api, jobId]);

  const update = (k, v) => {
    const next = { ...est, [k]: v };
    setEst(next);
    const laborCost = (next.labor_hours || 0) * (next.labor_rate || 0);
    const direct = laborCost + (next.material_cost || 0) + (next.equipment_cost || 0) + (next.subcontractor_cost || 0);
    const oh = direct * (next.overhead_percent || 0) / 100;
    const cont = direct * (next.contingency_percent || 0) / 100;
    const sub = direct + oh + cont;
    const profit = sub * (next.profit_margin_percent || 0) / 100;
    setCalc({ labor_cost: laborCost, direct_cost: direct, overhead: oh, contingency: cont, subtotal: sub, profit, total_estimate: sub + profit });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/accounting/jobs/${jobId}/estimate`, est);
      setEst(r.data.estimate); setCalc(r.data.calculations);
      toast.success('Estimate saved');
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  if (!jobId) return <p className="text-gray-500">Select a project to build an estimate.</p>;
  if (!est) return <p className="text-gray-500">Loading...</p>;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="fos-estimate-tab">
      <Card className="lg:col-span-2">
        <h3 className="font-semibold text-gray-900 mb-4">Estimate Inputs</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Labor Hours" value={est.labor_hours} onChange={(v) => update('labor_hours', v)} readOnly={readOnly} testid="est-labor-hours" />
          <Field label="Labor Rate ($/hr)" value={est.labor_rate} onChange={(v) => update('labor_rate', v)} readOnly={readOnly} testid="est-labor-rate" />
          <Field label="Material Cost" value={est.material_cost} onChange={(v) => update('material_cost', v)} readOnly={readOnly} testid="est-material-cost" />
          <Field label="Equipment Cost" value={est.equipment_cost} onChange={(v) => update('equipment_cost', v)} readOnly={readOnly} testid="est-equipment-cost" />
          <Field label="Subcontractor Cost" value={est.subcontractor_cost} onChange={(v) => update('subcontractor_cost', v)} readOnly={readOnly} testid="est-sub-cost" />
          <Field label="Overhead %" value={est.overhead_percent} onChange={(v) => update('overhead_percent', v)} readOnly={readOnly} testid="est-overhead-pct" suffix="%" />
          <Field label="Profit Margin %" value={est.profit_margin_percent} onChange={(v) => update('profit_margin_percent', v)} readOnly={readOnly} testid="est-profit-pct" suffix="%" />
          <Field label="Contingency %" value={est.contingency_percent} onChange={(v) => update('contingency_percent', v)} readOnly={readOnly} testid="est-contingency-pct" suffix="%" />
        </div>
        {!readOnly && (
          <button data-testid="est-save-btn" onClick={save} disabled={saving} className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[#C6A87C] text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Estimate'}
          </button>
        )}
      </Card>
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Calculated Estimate</h3>
        <div className="space-y-3 text-sm">
          <Row label="Labor Cost" value={fmt(calc?.labor_cost)} />
          <Row label="Direct Cost" value={fmt(calc?.direct_cost)} />
          <Row label="Overhead" value={fmt(calc?.overhead)} />
          <Row label="Contingency" value={fmt(calc?.contingency)} />
          <Row label="Subtotal" value={fmt(calc?.subtotal)} />
          <Row label="Profit" value={fmt(calc?.profit)} />
          <div className="pt-3 border-t border-gray-200">
            <Row label="Total Estimate / Sell Price" value={fmt(calc?.total_estimate)} bold accent={GOLD} testid="est-total" />
          </div>
        </div>
      </Card>
    </div>
  );
};
